import React from 'react';
import { Save, Trash2 } from 'lucide-react';
import { BottomSheet } from './ui/BottomSheet';
import { Dialog } from './ui/Dialog';
import { Button } from './ui/Button';
import { useIsDesktop } from '../hooks/useIsDesktop';

interface UnsavedChangesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  onDiscard: () => void;
  onContinueEditing?: () => void;
  isSaving?: boolean;
  saveButtonText?: string;
  discardButtonText?: string;
  continueEditingText?: string;
}

const UnsavedChangesDialog: React.FC<UnsavedChangesDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  onDiscard,
  onContinueEditing = onClose,
  isSaving = false,
  saveButtonText = 'Spara ändringar',
  discardButtonText = 'Avbryt ändringar',
  continueEditingText = 'Fortsätt redigera',
}) => {
  const isDesktop = useIsDesktop();

  if (isDesktop) {
    return (
      <Dialog
        open={isOpen}
        onClose={onContinueEditing}
        title="Osparade ändringar"
        role="alertdialog"
        actions={
          <>
            <Button variant="ghost" onClick={onContinueEditing} disabled={isSaving} data-autofocus>{continueEditingText}</Button>
            <Button variant="destructive" onClick={onDiscard} disabled={isSaving}>{discardButtonText}</Button>
            <Button variant="primary" onClick={onSave} loading={isSaving}>{saveButtonText}</Button>
          </>
        }
      >
        Du har ändringar som inte är sparade. Spara dem, eller lämna sidan utan att spara.
      </Dialog>
    );
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={onContinueEditing} variant="sheet" title="Osparade ändringar">
      <div className="px-3 pb-3 sm:px-4 sm:pb-4">
        <p className="text-sm text-[var(--text-secondary)] text-center mb-3 -mt-1">
          Du har osparade ändringar. Vad vill du göra?
        </p>

        {/* Action group */}
        <div className="bg-[var(--bg-surface-alt)] rounded-xl overflow-hidden">
          <button
            onClick={onSave}
            disabled={isSaving}
            className="w-full flex items-center gap-3 px-4 min-h-[52px] active:bg-[var(--bg-surface-hover)] transition-colors disabled:opacity-50 text-accent-600 dark:text-accent-400"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-accent-600 border-t-transparent flex-shrink-0" />
                <span className="text-[15px] font-medium">Sparar...</span>
              </>
            ) : (
              <>
                <Save size={20} className="flex-shrink-0" />
                <span className="text-[15px] font-medium">{saveButtonText}</span>
              </>
            )}
          </button>

          <div className="h-px bg-[var(--border-subtle)] mx-4" />

          <button
            onClick={onDiscard}
            disabled={isSaving}
            className="w-full flex items-center gap-3 px-4 min-h-[52px] active:bg-[var(--bg-surface-hover)] transition-colors disabled:opacity-50 text-red-600 dark:text-red-400"
          >
            <Trash2 size={20} className="flex-shrink-0" />
            <span className="text-[15px] font-medium">{discardButtonText}</span>
          </button>
        </div>

        {/* Cancel button — separate card (iOS pattern) */}
        <button
          onClick={onContinueEditing}
          disabled={isSaving}
          className="w-full mt-2 bg-[var(--bg-surface-alt)] rounded-xl min-h-[52px] text-[15px] font-semibold text-accent-600 dark:text-accent-400 active:bg-[var(--bg-surface-hover)] transition-colors disabled:opacity-50"
        >
          {continueEditingText}
        </button>
      </div>
    </BottomSheet>
  );
};

export default UnsavedChangesDialog;
